import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FilesApi } from '@up2stay/up2promo-api-client';
import {
  Button,
  ConfirmDialog,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import type { ModuleEntryGovernanceProps } from '@/core/components/profile/module-entry-governance';
import {
  useEntryGovernance,
  WholeModuleGovernanceHeader,
} from '@/core/components/profile/module-entry-governance-ui';
import { useDndProfileSensors } from '@/core/hooks/use-dnd-profile-sensors';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  lockBodyScrollDuringDrag,
  triggerDragEndHaptic,
  triggerDragOverHaptic,
  triggerDragStartHaptic,
  unlockBodyScrollAfterDrag,
} from '@/core/lib/dnd-haptics';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import { MODULE_ENTRY_WHOLE_MODULE_KEY } from '@/core/lib/profile-module-entry-policies';
import { useDictionary } from '@/core/providers/dictionary-provider';
import type {
  ImageItem,
  ImagesAspectRatio,
  ImagesModuleConfig,
} from '@/core/types/profile-modules';

const ASPECT_RATIO_OPTIONS: { value: ImagesAspectRatio; label: string }[] = [
  { value: '1:1', label: '1:1 (Quadrat)' },
  { value: '4:3', label: '4:3' },
  { value: '16:9', label: '16:9' },
  { value: '3:2', label: '3:2' },
  { value: '3:4', label: '3:4 (Hochformat)' },
];

function reorderItems<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  const result = [...items];
  const removed = result.splice(fromIndex, 1)[0];
  if (removed === undefined) return items;
  result.splice(toIndex, 0, removed);
  return result;
}

interface SortableImageItemProps {
  item: ImageItem;
  index: number;
  onRemove: () => void;
  isMobile: boolean;
  structureReadOnly?: boolean;
}

function SortableImageItem({
  item,
  index,
  onRemove,
  isMobile,
  structureReadOnly = false,
}: SortableImageItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `image-${index}`, disabled: structureReadOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : 'none',
  };

  const deleteButton = !structureReadOnly ? (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn(
        'text-white hover:bg-white/20',
        isMobile
          ? 'absolute top-0.5 right-0.5 h-7 w-7 rounded-full bg-black/50'
          : 'h-8 w-8',
      )}
      onClick={(e) => {
        e.stopPropagation();
        onRemove();
      }}
      onPointerDown={(e) => isMobile && e.stopPropagation()}
      aria-label="Bild entfernen"
    >
      <Trash2 className={isMobile ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
    </Button>
  ) : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex-shrink-0 w-24 h-24 rounded-lg border overflow-hidden relative group',
        isDragging && 'opacity-80 shadow-lg z-10 ring-2 ring-primary',
        isMobile &&
          !structureReadOnly &&
          'touch-pan-x cursor-grab active:cursor-grabbing',
      )}
      {...(isMobile && !structureReadOnly
        ? { ...listeners, ...attributes }
        : {})}
    >
      <img
        src={getAbsoluteFileUrl(item.file.path)}
        alt=""
        className={cn(
          'w-full h-full object-cover',
          isMobile && 'pointer-events-none',
        )}
      />
      {isMobile ? (
        deleteButton
      ) : !structureReadOnly ? (
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
          <div
            data-dnd-grip
            {...listeners}
            {...attributes}
            className="touch-none cursor-grab active:cursor-grabbing rounded p-1 hover:bg-white/20"
            aria-label="Reihenfolge ändern"
          >
            <GripVertical className="h-4 w-4 text-white" />
          </div>
          {deleteButton}
        </div>
      ) : null}
    </div>
  );
}

interface ImagesModuleConfigProps extends ModuleEntryGovernanceProps {
  config: ImagesModuleConfig;
  onChange: (config: ImagesModuleConfig) => void;
  fieldLockedHintMessage?: string;
}

export function ImagesModuleConfigComponent({
  config,
  onChange,
  templateModule,
  showEntryPolicies = false,
  onEntryPolicyChange,
  policyLabels,
  fieldLockedHintMessage,
  structureReadOnly: _structureReadOnly = false,
  contentReadOnly = false,
}: ImagesModuleConfigProps) {
  const dict = useDictionary();
  const imagesDict = dict.profiles.edit.modules.images;
  const isMobile = useIsMobile();
  const { entryLocked } = useEntryGovernance({
    templateModule,
    entryId: MODULE_ENTRY_WHOLE_MODULE_KEY,
  });
  const moduleContentLocked =
    contentReadOnly || (entryLocked && !showEntryPolicies);

  const [localConfig, setLocalConfig] = useState<ImagesModuleConfig>(config);
  const [prevConfig, setPrevConfig] = useState(config);
  if (config !== prevConfig) {
    setPrevConfig(config);
    setLocalConfig(config);
  }
  const [isUploading, setIsUploading] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRestoreRef = useRef<string | null>(null);
  const lastOverIdRef = useRef<UniqueIdentifier | null>(null);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );

  const handleChange = (updates: Partial<ImagesModuleConfig>) => {
    if (moduleContentLocked) return;
    const newConfig = { ...localConfig, ...updates };
    setLocalConfig(newConfig);
    onChange(newConfig);
  };

  const images = localConfig.images || [];

  const addImages = (newItems: ImageItem[]) => {
    const withOrder = newItems.map((item, i) => ({
      ...item,
      order: images.length + i,
    }));
    handleChange({ images: [...images, ...withOrder] });
  };

  const requestRemoveImage = (index: number) => {
    setPendingRemoveIndex(index);
  };

  const confirmRemoveImage = () => {
    if (pendingRemoveIndex == null) return;
    const index = pendingRemoveIndex;
    const reordered = images
      .filter((_, i) => i !== index)
      .map((img, i) => ({ ...img, order: i }));
    handleChange({ images: reordered });
    setPendingRemoveIndex(null);
  };

  const handleDragStart = () => {
    lastOverIdRef.current = null;
    scrollRestoreRef.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (over == null) {
      lastOverIdRef.current = null;
      return;
    }
    const isNewSlot = lastOverIdRef.current !== over.id;
    const isBackToOwnSlot =
      over.id === active.id && lastOverIdRef.current === null;
    if (isNewSlot && !isBackToOwnSlot) {
      void triggerDragOverHaptic();
    }
    lastOverIdRef.current = over.id;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (scrollRestoreRef.current !== null) {
      document.body.style.overflow = scrollRestoreRef.current;
      scrollRestoreRef.current = null;
    }
    const { active, over } = event;
    if (over == null || active.id === over.id) return;
    const fromIndex = images.findIndex((_, i) => `image-${i}` === active.id);
    const toIndex = images.findIndex((_, i) => `image-${i}` === over.id);
    if (fromIndex === -1 || toIndex === -1) return;
    const reordered = reorderItems(images, fromIndex, toIndex).map(
      (img, i) => ({
        ...img,
        order: i,
      }),
    );
    handleChange({ images: reordered });
  };

  const uploadFilesAndAdd = async (files: FileList | File[]) => {
    if (moduleContentLocked) return;
    const fileArray = Array.from(files);
    const imageFiles = fileArray.filter((f) => f.type.startsWith('image/'));
    if (!imageFiles.length) return;

    setIsUploading(true);
    const newItems: ImageItem[] = [];

    try {
      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        const res = await FilesApi.uploadFile({
          body: { file },
        });
        if (res.data?.file) {
          // Keep the just-minted signed URL in UI state for instant preview.
          // sanitizeModulesForProfileApi persists id + storage key only (DEV-458).
          newItems.push({
            id: crypto.randomUUID(),
            file: { id: res.data.file.id, path: res.data.file.path },
            order: images.length + newItems.length,
          });
        }
      }
      if (newItems.length > 0) {
        addImages(newItems);
        toast.success(
          newItems.length === 1
            ? imagesDict.addedOne
            : imagesDict.addedMany.replace('{count}', String(newItems.length)),
        );
      }
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(imagesDict.uploadFailed);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    await uploadFilesAndAdd(files);
    e.target.value = '';
  };

  const handleFileDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDraggingFile(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDraggingFile(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    setIsDraggingFile(false);
    if (!e.dataTransfer.files?.length) return;
    e.preventDefault();
    await uploadFilesAndAdd(e.dataTransfer.files);
  };

  const sensors = useDndProfileSensors();

  const aspectRatio = localConfig.aspectRatio ?? '1:1';

  return (
    <div className="space-y-3">
      <WholeModuleGovernanceHeader
        templateModule={templateModule}
        showEntryPolicy={showEntryPolicies}
        onEntryPolicyChange={onEntryPolicyChange}
        policyLabels={policyLabels}
        fieldLockedHintMessage={fieldLockedHintMessage}
        enableMemberValues={false}
      />
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">
          Seitenverhältnis
        </label>
        <Select
          value={aspectRatio}
          onValueChange={(value) =>
            handleChange({ aspectRatio: value as ImagesAspectRatio })
          }
          disabled={moduleContentLocked}
        >
          <SelectTrigger className="w-full max-w-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ASPECT_RATIO_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div
        className={cn(
          'flex gap-2 overflow-x-auto pt-2 pb-2 min-h-[6.5rem] rounded-lg transition-colors',
          isDraggingFile && 'ring-2 ring-primary ring-offset-2 bg-muted/50',
          moduleContentLocked && 'pointer-events-none opacity-70',
        )}
        onDragOver={handleFileDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis]}
          onDragStart={() => {
            handleDragStart();
            lockBodyScrollDuringDrag();
            void triggerDragStartHaptic();
          }}
          onDragOver={handleDragOver}
          onDragEnd={(e) => {
            handleDragEnd(e);
            unlockBodyScrollAfterDrag();
            void triggerDragEndHaptic();
          }}
        >
          <SortableContext
            items={images.map((_, i) => `image-${i}`)}
            strategy={horizontalListSortingStrategy}
          >
            {images.map((item, index) => (
              <SortableImageItem
                key={item.id ?? item.file.id ?? index}
                item={item}
                index={index}
                onRemove={() => requestRemoveImage(index)}
                isMobile={isMobile}
                structureReadOnly={moduleContentLocked}
              />
            ))}
          </SortableContext>
        </DndContext>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileChange}
          disabled={isUploading || moduleContentLocked}
        />
        {!moduleContentLocked ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className={cn(
              'group flex-shrink-0 w-24 h-24 rounded-lg border-2 border-dashed border-muted-foreground/30',
              'flex items-center justify-center transition-colors',
              'hover:border-primary hover:bg-primary/5 disabled:opacity-50',
            )}
            aria-label="Bild hinzufügen"
          >
            {isUploading ? (
              <span className="text-xs text-muted-foreground">…</span>
            ) : (
              <Plus className="h-8 w-8 text-muted-foreground transition-colors group-hover:text-primary" />
            )}
          </button>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground">
        Plus-Feld oder Bilder hierher ziehen zum Hochladen. Reihenfolge per Drag
        ändern.
      </p>

      <ConfirmDialog
        open={pendingRemoveIndex !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemoveIndex(null);
        }}
        title="Bild entfernen?"
        description="Möchtest du dieses Bild wirklich entfernen? Diese Aktion kann nicht rückgängig gemacht werden."
        confirmLabel="Entfernen"
        cancelLabel="Abbrechen"
        destructive
        onConfirm={confirmRemoveImage}
      />
    </div>
  );
}
